import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Calculator, UserPlus } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { createCustomer, updateCustomer, customerIdExists } from '../utils/db/customers';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProfile } from '../utils/db/profiles';
import { getRateConfiguration, upsertRateConfiguration } from '../utils/db/rates';

const RateMaster = () => {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('add_customer'); // 'add_customer' or 'product_{id}'
    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [rateData, setRateData] = useState({}); // { [productId_mode]: { slabType: 'slab2', config: {}, rates: {} } }
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [hasProfile, setHasProfile] = useState(null); // null = loading, true/false
    const [profileGst, setProfileGst] = useState('');

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

    useEffect(() => {
        if (location.state?.editCustomer) {
            const customer = location.state.editCustomer;
            setEditingCustomer(customer);
            setActiveTab('add_customer');

            // Populate form
            setValue('customerId', customer.customer_id);
            setValue('customerName', customer.customer_name);
            setValue('address', customer.address || '');
            setValue('email', customer.email || '');
            setValue('mobile', customer.mobile || '');
            setValue('gstNo', customer.gst_no || '');
            setValue('fuelCharge', customer.fuel_charge || 0);
            setValue('royaltyCharge', customer.royalty_charge || 0);
            setValue('gstPercent', customer.gst_percent || 0);
        }
    }, [location.state, setValue]);

    React.useEffect(() => {
        if (user) {
            getProfile(user.id).then(profile => {
                if (profile && (profile.franchise_name || profile.products?.length > 0)) {
                    setHasProfile(true);
                    if (profile.products) setProducts(profile.products);
                    if (profile.sectors) setSectors(profile.sectors);
                    if (profile.gst_no) setProfileGst(profile.gst_no);
                } else {
                    setHasProfile(false);
                }
            }).catch(err => {
                console.error("Error fetching profile:", err);
                setHasProfile(false);
            });
        }
    }, [user]);

    // Fetch rate config when active tab changes to a product OR when editingCustomer changes
    React.useEffect(() => {
        if (user && activeTab.startsWith('product_')) {
            const productId = activeTab.replace('product_', '');
            const product = products.find(p => p.id === productId);

            // If we are definitely creating a new customer (editingCustomer is null),
            // we should not fetch any rates yet, because products tab is disabled anyway.
            // But if we just Saved, we have editingCustomer.

            // To support distinct rates per customer, we pass editingCustomer.id
            const customerId = editingCustomer?.id || null;

            if (product && product.modes) {
                // Fetch for ALL modes of this product
                product.modes.forEach(mode => {
                    const compositeKey = `${productId}_${mode}`;
                    // Force refresh or check if loaded? 
                    // Better to re-fetch if customer changed. Use a simple cache check?
                    // Actually, if customer changes, rateData should probably be cleared? 
                    // For now, let's just fetch.

                    getRateConfiguration(user.id, productId, mode, customerId)
                        .then(config => {
                            if (config) {
                                setRateData(prev => ({
                                    ...prev,
                                    [compositeKey]: {
                                        slabType: config.slab_type,
                                        config: config.config,
                                        rates: config.rates
                                    }
                                }));
                            } else {
                                // Important: If no config exists for this customer, 
                                // we should reset to default/empty so we don't show previous customer's data!
                                setRateData(prev => ({
                                    ...prev,
                                    [compositeKey]: {
                                        slabType: 'slab2',
                                        config: { slab2: { base: 0.5, add: 1 } },
                                        rates: {}
                                    }
                                }));
                            }
                        })
                        .catch(err => console.error(`Error fetching rates for ${mode}:`, err));
                });
            }
        }
    }, [user, activeTab, products, editingCustomer]); // Added editingCustomer dependency

    const handleSlabTypeChange = (compositeKey, type) => {
        setRateData(prev => {
            const existing = prev[compositeKey] || {};
            const defaultConfigs = {
                slab2: { base: 0.5, add: 1 },
                slab3: { base1: 0.5, base2: 2, add: 1 },
                slab4: { base1: 0.5, base2: 2, base3: 3, add: 1 }
            };

            return {
                ...prev,
                [compositeKey]: {
                    ...existing,
                    slabType: type,
                    config: {
                        ...existing.config,
                        [type]: existing.config?.[type] || defaultConfigs[type]
                    },
                    rates: existing.rates || {}
                }
            };
        });
    };

    const handleConfigChange = (compositeKey, field, value) => {
        setRateData(prev => {
            const productData = prev[compositeKey] || {
                slabType: 'slab2',
                config: { slab2: { base: 0.5, add: 1 } },
                rates: {}
            };
            const currentSlabType = productData.slabType;

            return {
                ...prev,
                [compositeKey]: {
                    ...productData,
                    config: {
                        ...productData.config,
                        [currentSlabType]: {
                            ...productData.config?.[currentSlabType],
                            [field]: value
                        }
                    }
                }
            };
        });
    };

    const handleRateChange = (compositeKey, sectorId, field, value) => {
        setRateData(prev => {
            const productData = prev[compositeKey] || {
                slabType: 'slab2',
                config: { slab2: { base: 0.5, add: 1 } },
                rates: {}
            };

            const existingSectorRates = productData.rates?.[sectorId] || {};

            return {
                ...prev,
                [compositeKey]: {
                    ...productData,
                    rates: {
                        ...productData.rates,
                        [sectorId]: {
                            ...existingSectorRates,
                            [field]: value
                        }
                    }
                }
            };
        });
    };

    const handleSaveRates = async (compositeKey) => {
        const currentData = rateData[compositeKey];
        if (!currentData) return;

        const lastUnderscoreIndex = compositeKey.lastIndexOf('_');
        const productId = compositeKey.substring(0, lastUnderscoreIndex);
        const mode = compositeKey.substring(lastUnderscoreIndex + 1);

        // CLEANUP: Keep only rates relevant to active slabType
        const slabType = currentData.slabType;
        const cleanedRates = {};
        // Map slabType to prefix: slab2 -> s2_, slab3 -> s3_, slab4 -> s4_
        const prefix = slabType === 'slab2' ? 's2_' : (slabType === 'slab3' ? 's3_' : 's4_');

        if (currentData.rates) {
            Object.keys(currentData.rates).forEach(sectorId => {
                const sectorRates = currentData.rates[sectorId];
                if (!sectorRates) return;

                const newSectorRates = {};
                Object.keys(sectorRates).forEach(key => {
                    // Only keep keys that strictly start with the prefix for the current slab
                    if (key.startsWith(prefix)) {
                        newSectorRates[key] = sectorRates[key];
                    }
                });
                cleanedRates[sectorId] = newSectorRates;
            });
        }

        const dataToSave = {
            ...currentData,
            config: currentData.config,
            rates: cleanedRates
        };

        setIsLoading(true);
        try {
            const customerId = editingCustomer?.id || null;
            if (!customerId) {
                toast.error("No customer selected. Cannot save rates.");
                return;
            }
            await upsertRateConfiguration(user.id, productId, mode, dataToSave, customerId);

            // Update local state so UI reflects the cleanup (other slab data vanishes)
            setRateData(prev => ({
                ...prev,
                [compositeKey]: dataToSave
            }));

            toast.success(`Rates for ${mode} saved successfully for customer`);
        } catch (error) {
            console.error('Error saving rates:', error);
            console.error('Error details:', error.message, error.details, error.hint);
            toast.error(`Failed to save rates: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const onAddCustomerSubmit = async (data) => {
        setIsLoading(true);
        try {
            // Auto-determine state based on GST numbers on submit
            let finalState = 'same'; // Default
            if (data.gstNo && data.gstNo.length >= 2 && profileGst && profileGst.length >= 2) {
                const customerStateCode = data.gstNo.substring(0, 2);
                const profileStateCode = profileGst.substring(0, 2);
                finalState = customerStateCode === profileStateCode ? 'same' : 'other';
            } else if (editingCustomer && editingCustomer.customer_state) {
                finalState = editingCustomer.customer_state;
            }
            data.customerState = finalState;

            let savedCustomer = null;
            if (editingCustomer) {
                savedCustomer = await updateCustomer(user.id, editingCustomer.id, data);
                toast.success('Customer updated successfully');
            } else {
                const exists = await customerIdExists(user.id, data.customerId);
                if (exists) {
                    toast.error('Customer ID already exists');
                    setIsLoading(false);
                    return;
                }
                savedCustomer = await createCustomer(user.id, data);
                toast.success('Customer added successfully');
            }

            // After save/update, set as editing customer and switch to first product tab
            setEditingCustomer(savedCustomer);
            if (products.length > 0) {
                setActiveTab(`product_${products[0].id}`);
            }
        } catch (error) {
            console.error('Error saving customer:', error);
            toast.error('Failed to save customer');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Render Helpers ---

    const renderSlab2 = (compositeKey, currentData) => (
        <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-primary-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3"></th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-2">
                                    <span>Upto</span>
                                    <input
                                        type="number" step="any"
                                        value={currentData.config.slab2?.base || ''}
                                        onChange={(e) => handleConfigChange(compositeKey, 'base', e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center bg-white"
                                    />
                                    <span>Kg</span>
                                </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-2">
                                    <span>Additional</span>
                                    <input
                                        type="number" step="any"
                                        value={currentData.config.slab2?.add || ''}
                                        onChange={(e) => handleConfigChange(compositeKey, 'add', e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center bg-white"
                                    />
                                    <span>Kg</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sectors.map((sector) => (
                            <tr key={sector.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sector.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input
                                        type="number" step="any"
                                        value={currentData.rates[sector.id]?.s2_base || ''}
                                        onChange={(e) => handleRateChange(compositeKey, sector.id, 's2_base', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
                                        placeholder="0"
                                    />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input
                                        type="number" step="any"
                                        value={currentData.rates[sector.id]?.s2_add || ''}
                                        onChange={(e) => handleRateChange(compositeKey, sector.id, 's2_add', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
                                        placeholder="0"
                                    />
                                </td>
                            </tr>
                        ))}
                        {sectors.length === 0 && (
                            <tr><td colSpan="3" className="px-6 py-4 text-center text-gray-500">No sectors configured.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end">
                <button onClick={() => handleSaveRates(compositeKey)} disabled={isLoading} className="px-6 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50">{isLoading ? 'Saving...' : 'Save Rates'}</button>
            </div>
        </div>
    );

    const renderSlab3 = (compositeKey, currentData) => (
        <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-primary-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4"></th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-2">
                                    <span>Upto</span>
                                    <input
                                        type="number" step="any"
                                        value={currentData.config.slab3?.base1 || ''}
                                        onChange={(e) => handleConfigChange(compositeKey, 'base1', e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center bg-white"
                                    />
                                    <span>Kg</span>
                                </div>
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-2">
                                    <span>Upto</span>
                                    <input
                                        type="number" step="any"
                                        value={currentData.config.slab3?.base2 || ''}
                                        onChange={(e) => handleConfigChange(compositeKey, 'base2', e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center bg-white"
                                    />
                                    <span>Kg</span>
                                </div>
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-2">
                                    <span>Additional</span>
                                    <input
                                        type="number" step="any"
                                        value={currentData.config.slab3?.add || ''}
                                        onChange={(e) => handleConfigChange(compositeKey, 'add', e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center bg-white"
                                    />
                                    <span>Kg</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sectors.map((sector) => (
                            <tr key={sector.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sector.name}</td>
                                <td className="px-4 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input
                                        type="number" step="any"
                                        value={currentData.rates[sector.id]?.s3_base1 || ''}
                                        onChange={(e) => handleRateChange(compositeKey, sector.id, 's3_base1', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
                                        placeholder="0"
                                    />
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input
                                        type="number" step="any"
                                        value={currentData.rates[sector.id]?.s3_base2 || ''}
                                        onChange={(e) => handleRateChange(compositeKey, sector.id, 's3_base2', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
                                        placeholder="0"
                                    />
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input
                                        type="number" step="any"
                                        value={currentData.rates[sector.id]?.s3_add || ''}
                                        onChange={(e) => handleRateChange(compositeKey, sector.id, 's3_add', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
                                        placeholder="0"
                                    />
                                </td>
                            </tr>
                        ))}
                        {sectors.length === 0 && (
                            <tr><td colSpan="4" className="px-6 py-4 text-center text-gray-500">No sectors configured.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end">
                <button onClick={() => handleSaveRates(compositeKey)} disabled={isLoading} className="px-6 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50">{isLoading ? 'Saving...' : 'Save Rates'}</button>
            </div>
        </div>
    );

    const renderSlab4 = (compositeKey, currentData) => (
        <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-primary-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5"></th>
                            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-1">
                                    <span>Upto</span>
                                    <input type="number" step="any" value={currentData.config.slab4?.base1 || ''} onChange={(e) => handleConfigChange(compositeKey, 'base1', e.target.value)} className="w-12 px-1 py-1 border border-gray-300 rounded text-center bg-white text-xs" />
                                    <span>Kg</span>
                                </div>
                            </th>
                            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-1">
                                    <span>Upto</span>
                                    <input type="number" step="any" value={currentData.config.slab4?.base2 || ''} onChange={(e) => handleConfigChange(compositeKey, 'base2', e.target.value)} className="w-12 px-1 py-1 border border-gray-300 rounded text-center bg-white text-xs" />
                                    <span>Kg</span>
                                </div>
                            </th>
                            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-1">
                                    <span>Upto</span>
                                    <input type="number" step="any" value={currentData.config.slab4?.base3 || ''} onChange={(e) => handleConfigChange(compositeKey, 'base3', e.target.value)} className="w-12 px-1 py-1 border border-gray-300 rounded text-center bg-white text-xs" />
                                    <span>Kg</span>
                                </div>
                            </th>
                            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                                <div className="flex items-center justify-center space-x-1">
                                    <span>Add.</span>
                                    <input type="number" step="any" value={currentData.config.slab4?.add || ''} onChange={(e) => handleConfigChange(compositeKey, 'add', e.target.value)} className="w-12 px-1 py-1 border border-gray-300 rounded text-center bg-white text-xs" />
                                    <span>Kg</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sectors.map((sector) => (
                            <tr key={sector.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sector.name}</td>
                                <td className="px-2 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input type="number" step="any" value={currentData.rates[sector.id]?.s4_base1 || ''} onChange={(e) => handleRateChange(compositeKey, sector.id, 's4_base1', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center" placeholder="0" />
                                </td>
                                <td className="px-2 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input type="number" step="any" value={currentData.rates[sector.id]?.s4_base2 || ''} onChange={(e) => handleRateChange(compositeKey, sector.id, 's4_base2', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center" placeholder="0" />
                                </td>
                                <td className="px-2 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input type="number" step="any" value={currentData.rates[sector.id]?.s4_base3 || ''} onChange={(e) => handleRateChange(compositeKey, sector.id, 's4_base3', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center" placeholder="0" />
                                </td>
                                <td className="px-2 py-4 whitespace-nowrap border-l border-gray-200">
                                    <input type="number" step="any" value={currentData.rates[sector.id]?.s4_add || ''} onChange={(e) => handleRateChange(compositeKey, sector.id, 's4_add', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 text-center" placeholder="0" />
                                </td>
                            </tr>
                        ))}
                        {sectors.length === 0 && (
                            <tr><td colSpan="5" className="px-6 py-4 text-center text-gray-500">No sectors configured.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end">
                <button onClick={() => handleSaveRates(compositeKey)} disabled={isLoading} className="px-6 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50">{isLoading ? 'Saving...' : 'Save Rates'}</button>
            </div>
        </div>
    );

    const renderModeSection = (productId, mode) => {
        const compositeKey = `${productId}_${mode}`;
        const currentData = rateData[compositeKey] || { slabType: 'slab2', config: { slab2: { base: 0.5, add: 1 } }, rates: {} };

        return (
            <div key={mode} className="space-y-6 pt-6 border-t border-gray-200 first:border-0 first:pt-0">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 bg-gray-100 px-3 py-1 rounded inline-block">
                        Mode: <span className="text-primary-600 font-bold">{mode}</span>
                    </h3>

                    {/* Slab Selection per mode */}
                    <div className="flex space-x-6">
                        {['slab2', 'slab3', 'slab4'].map((type, idx) => (
                            <label key={type} className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name={`slabType_${compositeKey}`}
                                    checked={currentData.slabType === type}
                                    onChange={() => handleSlabTypeChange(compositeKey, type)}
                                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                />
                                <span className="ml-2 text-gray-700 font-medium capitalize text-sm">
                                    Slab {idx + 2}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                {currentData.slabType === 'slab2' && renderSlab2(compositeKey, currentData)}
                {currentData.slabType === 'slab3' && renderSlab3(compositeKey, currentData)}
                {currentData.slabType === 'slab4' && renderSlab4(compositeKey, currentData)}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Rate Master</h1>
                    <p className="text-gray-600">Manage freight rates and zones</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 overflow-x-auto">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('add_customer')}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center
              ${activeTab === 'add_customer' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add Customer
                    </button>

                    {products.map((product) => (
                        <button
                            key={product.id}
                            disabled={!editingCustomer}
                            onClick={() => setActiveTab(`product_${product.id}`)}
                            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center
                ${activeTab === `product_${product.id}` ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                ${!editingCustomer ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={!editingCustomer ? "Please select/edit a customer to configure rates" : ""}
                        >
                            <Calculator className="w-4 h-4 mr-2" />
                            {product.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow p-6">
                {hasProfile === false ? (
                    <div className="text-center py-12">
                        <div className="h-16 w-16 bg-orange-100 text-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <UserPlus className="h-8 w-8" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Profile Incomplete</h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto">
                            You must securely save your business profile information before you can start adding customers and assigning rates.
                        </p>
                        <button 
                            onClick={() => navigate('/profile')} 
                            className="px-6 py-2.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors shadow-sm"
                        >
                            Complete Profile Setup Now
                        </button>
                    </div>
                ) : activeTab === 'add_customer' ? (
                    <div className="max-w-3xl mx-auto">
                        <h3 className="text-lg font-medium text-gray-900 mb-6">{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h3>
                        <form onSubmit={handleSubmit(onAddCustomerSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Customer ID *</label>
                                <input type="text" {...register('customerId', { required: 'Customer ID is required', maxLength: { value: 30, message: 'Max 30 chars' } })} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter customer ID" />
                                {errors.customerId && <p className="mt-1 text-sm text-red-600">{errors.customerId.message}</p>}
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name *</label>
                                <input type="text" {...register('customerName', { required: 'Customer name is required', maxLength: { value: 100, message: 'Max 100 chars' } })} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter customer name" />
                                {errors.customerName && <p className="mt-1 text-sm text-red-600">{errors.customerName.message}</p>}
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                                <textarea {...register('address', { required: 'Address is required', maxLength: { value: 100, message: 'Max 100 chars' } })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter address" />
                                {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>}
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                                <input type="email" {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } })} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter email" />
                                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile *</label>
                                <input type="tel" {...register('mobile', { required: 'Mobile is required', pattern: { value: /^[0-9]{10}$/, message: 'Invalid mobile' } })} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter mobile" />
                                {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile.message}</p>}
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                                <input type="text" {...register('gstNo')} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter GST (optional)" />
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Charge (%)</label>
                                <input type="number" step="0.01" {...register('fuelCharge', { min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter fuel charge %" />
                                {errors.fuelCharge && <p className="mt-1 text-sm text-red-600">{errors.fuelCharge.message}</p>}
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">SURCHARGE (%)</label>
                                <input type="number" step="0.01" {...register('royaltyCharge', { min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter surcharge %" />
                                {errors.royaltyCharge && <p className="mt-1 text-sm text-red-600">{errors.royaltyCharge.message}</p>}
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">GST Percentage (%)</label>
                                <input type="number" step="0.01" {...register('gstPercent', { min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter GST %" />
                                {errors.gstPercent && <p className="mt-1 text-sm text-red-600">{errors.gstPercent.message}</p>}
                            </div>
                            <div className="md:col-span-2 flex justify-end pt-4">
                                {editingCustomer && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditingCustomer(null);
                                            reset();
                                            navigate('/customers');
                                        }}
                                        className="mr-4 px-6 py-2 text-gray-700 bg-gray-100 font-medium rounded-md hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button type="submit" disabled={isLoading} className="px-6 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50">
                                    {isLoading ? 'Saving...' : (editingCustomer ? 'Update Customer' : 'Add Customer')}
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div>
                        {activeTab.startsWith('product_') && (
                            (() => {
                                const productId = activeTab.replace('product_', '');
                                const product = products.find(p => p.id === productId);

                                if (!product) return <div className="text-center py-12">Product not found</div>;

                                // Modes defaults to empty if undefined?
                                const modes = product.modes && product.modes.filter(Boolean).length > 0 ? product.modes.filter(Boolean) : [];

                                if (modes.length === 0) {
                                    return <div className="text-center py-12 text-gray-500">No modes configured for this product. Please add modes in Profile.</div>;
                                }

                                return (
                                    <div className="space-y-12">
                                        {modes.map(mode => renderModeSection(productId, mode))}
                                    </div>
                                );
                            })()
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RateMaster;
